import { useCallback, useMemo } from 'react';

import { gql } from '@apollo/client';
import type { Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

function sortByCreatedAt(automations: Apollo.AiReminderAutomationWithRelationsFragment[]) {
  return [...automations].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

interface UseAiReminderAutomationsResult {
  automations: Apollo.AiReminderAutomationWithRelationsFragment[];
  loading: boolean;
  refetch: () => void;
}

/**
 * Hook that manages AI reminder automations with real-time subscription updates.
 * Subscribes to org-wide automation events; Apollo auto-merges into cache.
 */
export function useAiReminderAutomations(
  reportId: UUID,
  options?: Omit<Parameters<typeof Apollo.useGetAiReminderAutomationsByReportQuery>[0], 'variables'>
): UseAiReminderAutomationsResult {
  const { data, loading, refetch } = Apollo.useGetAiReminderAutomationsByReportQuery({
    ...options,
    variables: { reportId },
  });
  const automations = useMemo(
    () => sortByCreatedAt(data?.aiReminderAutomationsByReport ?? []),
    [data?.aiReminderAutomationsByReport]
  );

  return {
    automations,
    loading,
    refetch,
  };
}

interface UseGetAiReminderAutomationResult {
  automation: Apollo.GetAiReminderAutomationQuery['aiReminderAutomation'] | null;
  loading: boolean;
}

export function useGetAiReminderAutomation(
  automationId: UUID,
  options?: Omit<Parameters<typeof Apollo.useGetAiReminderAutomationQuery>[0], 'variables'>
): UseGetAiReminderAutomationResult {
  const { data, loading } = Apollo.useGetAiReminderAutomationQuery({
    ...options,
    variables: { automationId },
  });

  return {
    automation: data?.aiReminderAutomation ?? null,
    loading,
  };
}

export function useCreateAiReminderAutomation() {
  const [createAutomation, { loading }] = Apollo.useCreateAiReminderAutomationMutation();

  const safeCreateAutomation = useCallback(
    (options: Parameters<typeof createAutomation>[0]) =>
      createAutomation({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;
          const newRef = cache.writeFragment({
            data: data.createAIReminderAutomation,
            fragment: gql`
              fragment NewAutomation on AIReminderAutomation {
                __typename
                id
              }
            `,
          });
          if (!newRef) return;

          const aiReminderAutomationsKey: keyof Apollo.Query = 'aiReminderAutomations';
          cache.modify({
            fields: {
              [aiReminderAutomationsKey]: (existing: readonly Reference[] = []) => {
                const reportId = options?.variables?.input.reportId;
                if (!reportId) return existing;
                return [...existing, newRef];
              },
            },
          });
        },
      }),
    [createAutomation]
  );

  return { createAutomation: safeCreateAutomation, loading };
}

export function useUpdateAiReminderAutomation() {
  const [updateAutomation, { loading }] = Apollo.useUpdateAiReminderAutomationMutation();

  return { loading, updateAutomation };
}

export function useDeleteAiReminderAutomation() {
  const [deleteAutomation, { loading }] = Apollo.useDeleteAiReminderAutomationMutation();

  const safeDeleteAutomation = useCallback(
    (options: Parameters<typeof deleteAutomation>[0]) =>
      deleteAutomation({
        ...options,
        update: (cache, { data }) => {
          if (!data) return;
          const deletedId = data.deleteAIReminderAutomation;
          cache.modify({
            fields: {
              aiReminderAutomationsByReport: (existing: readonly Reference[] = [], { readField }) =>
                existing.filter(ref => readField<string>('id', ref) !== deletedId),
            },
          });
          cache.evict({
            id: cache.identify({ __typename: 'AIReminderAutomation', id: deletedId }),
          });
          cache.gc();
        },
      }),
    [deleteAutomation]
  );

  return { deleteAutomation: safeDeleteAutomation, loading };
}

export function useRunAiReminderAutomation() {
  const [runAutomation, { loading }] = Apollo.useRunAiReminderAutomationMutation();

  return { loading, runAutomation };
}
